// Abgeleitete Navigation (RBAC-SPEC D.9, Teil E).
//
// Ein Menüpunkt erscheint, wenn der Benutzer mindestens eine bedingungslose
// `…ansehen`-Permission des zugehörigen Moduls bzw. der Section besitzt. Die
// Soll-Navigation aus Teil E ist Test-Fixture, keine Konfiguration — der Test
// in __tests__/navigation.test.ts prüft die Ableitung für alle 7 Rollen.

import { P, PERMISSION_KEYS, type PermissionKey } from './permissions.generated';
import { can, type EffectivePermissions } from './permissions';

export interface NavRule {
  /** Modul oder Modul.Section, deren ansehen-Keys den Punkt freischalten. */
  prefixes: readonly string[];
  /** Zusätzlich muss mindestens einer dieser Keys vorliegen. */
  alsoAnyOf?: readonly PermissionKey[];
  /** Zusatzbedingung an den Scope des freischaltenden Keys. */
  scope?: 'location_exact' | 'company_unrestricted' | 'location_or_company';
}

export interface NavNode {
  id: string;
  label: string;
  level: 1 | 2;
  rule?: NavRule;
  children?: NavNode[];
  /** Trennlinie vor diesem Unterpunkt, wenn davor schon einer steht (T-0152). */
  trennerDavor?: boolean;
}

// Kundenprofil-Keys erzeugen keinen Menüpunkt (Entscheidung 4): Finanzdaten eines
// Kunden sieht Office im Personenprofil, nicht im Bereich „Finanzen“.
// SPEC-OPEN: NAV
const NAV_EXCLUDED_KEYS: readonly PermissionKey[] = [
  P.buchhaltung.finanzdaten_und_zahlungen.finanzdaten_ansehen,
  // Entscheidung F03 (2026-09-21): Finance liest Kündigungen im Kunden- und
  // Finanzkontext, bekommt dafür aber keinen Menüpunkt „Anmeldung & Start" (Teil E).
  P.anmeldung.kuendigungen_ansehen,
];

// SPEC-OPEN: NAV — Personenlisten nur mit Nutzung (Entscheidung 10): Mitglieder
// sehen oder Listen für Newsletter/Kundenbereich verwenden. So erscheint der Punkt
// bei Recruiting nicht (Teil E), ohne dass eine Rolle beim Namen genannt wird.
const PERSONENLISTEN_NUTZUNG: readonly PermissionKey[] = [
  P.personenlisten.personen_einer_liste_ansehen,
  P.personenlisten.fuer_newsletter_verwenden,
  P.personenlisten.fuer_kundenbereich_anzeige_verwenden,
];

// SPEC-OPEN: NAV — Administration trennt über den Scope statt über Rollen:
// Standort-Konfiguration erscheint mit genau location-Scope (Standortleitung),
// globale Konfiguration nur mit unbeschränktem company-Scope. Damit sieht der CEO
// (company, begrenzt auf den Verantwortungsbereich) keine Administration, der
// Standort-Admin keine globalen Vorlagen und der Super-Admin die globalen Punkte.
// Entscheidung F33 (2026-09-21): Die Standort-Konfiguration erscheint zusätzlich für
// unbeschränktes company — sonst erreichte der Super-Admin die Standort-Regeln
// (Kündigungsfristen, Gebühren) nur über die direkte Adresse.
const standortSection = (id: string, label: string, section: string): NavNode => ({
  id,
  label,
  level: 2,
  rule: { prefixes: [`standort.${section}`], scope: 'location_or_company' },
});
const adminSection = (id: string, label: string, ...sections: string[]): NavNode => ({
  id,
  label,
  level: 2,
  rule: { prefixes: sections.map((section) => `admin.${section}`), scope: 'company_unrestricted' },
});

export const NAV_TREE: readonly NavNode[] = [
  { id: 'dashboard', label: 'Dashboard', level: 1, rule: { prefixes: ['dashboard'] } },
  {
    id: 'arbeitsbereiche',
    label: 'Arbeitsbereiche',
    level: 1,
    children: [
      { id: 'aufgaben', label: 'Aufgaben', level: 2, rule: { prefixes: ['arbeit.aufgaben'] } },
      { id: 'kalender', label: 'Kalender', level: 2, rule: { prefixes: ['arbeit.kalendereintraege'] } },
      { id: 'anfragen', label: 'Anfragen', level: 2, rule: { prefixes: ['anfragen'] }, trennerDavor: true },
      { id: 'anmeldung', label: 'Anmeldung & Start', level: 2, rule: { prefixes: ['anmeldung'] } },
      // Bestandskunden aus dem Altsystem: eigener Punkt neben der Anmeldung, nicht als
      // zweite Seite darunter — sonst stünde „Anmeldung & Start" doppelt im Menü.
      { id: 'migrationen', label: 'Offene Migrationen', level: 2, rule: { prefixes: ['anmeldung'] } },
    ],
  },
  {
    id: 'personen-bereich',
    label: 'Personen',
    level: 1,
    children: [
      { id: 'personen', label: 'Personen', level: 2, rule: { prefixes: ['personen'] } },
      {
        id: 'personenlisten',
        label: 'Personenlisten',
        level: 2,
        rule: { prefixes: ['personenlisten'], alsoAnyOf: PERSONENLISTEN_NUTZUNG },
      },
    ],
  },
  {
    id: 'unterricht',
    label: 'Unterricht',
    level: 1,
    children: [
      { id: 'termine', label: 'Terminübersicht', level: 2, rule: { prefixes: ['termine'] } },
      { id: 'raumplan', label: 'Raumplan', level: 2, rule: { prefixes: ['raumplan'] } },
      // T-0152: Lehrerwechsel und Kündigungen sind laufender Unterricht, keine
      // Unterseiten von Terminübersicht bzw. Anmeldung. Kündigungen erscheinen nur,
      // wo auch „Anmeldung & Start“ erscheint (Entscheidung F03: Finance nicht).
      { id: 'lehrerwechsel', label: 'Lehrerwechsel', level: 2, rule: { prefixes: ['termine'] }, trennerDavor: true },
      {
        id: 'kuendigungen',
        label: 'Kündigungen',
        level: 2,
        rule: { prefixes: ['anmeldung'], alsoAnyOf: [P.anmeldung.kuendigungen_ansehen] },
      },
    ],
  },
  { id: 'finanzen', label: 'Finanzen', level: 1, rule: { prefixes: ['buchhaltung'] } },
  { id: 'recruiting', label: 'Recruiting', level: 1, rule: { prefixes: ['recruiting'] } },
  { id: 'marketing', label: 'Marketing', level: 1, rule: { prefixes: ['marketing'] } },
  { id: 'website', label: 'Website & CMS', level: 1, rule: { prefixes: ['cms'] } },
  { id: 'auswertungen', label: 'Auswertungen', level: 1, rule: { prefixes: ['auswertungen'] } },
  {
    id: 'administration',
    label: 'Administration',
    level: 1,
    children: [
      standortSection('standort-stammdaten', 'Standort-Stammdaten', 'standort_stammdaten'),
      standortSection('standort-raeume', 'Räume & Ausstattung', 'raeume_und_ausstattung'),
      standortSection('standort-angebot', 'Unterrichtsangebot', 'unterrichtsangebot'),
      standortSection('standort-formen', 'Unterrichtsformen & Pakete', 'unterrichtsformen_und_pakete'),
      standortSection('standort-regularien', 'Vertragsregularien', 'vertragsregularien'),
      standortSection('standort-benutzer', 'Benutzer & Zugriffsrechte', 'benutzer_und_zugriffsrechte'),
      // Entscheidung F33: Der Standort-Admin verwaltet Vorlagen seiner Standorte (D.7),
      // hatte dafür aber keinen Menüpunkt. Global bleibt der Punkt unter Administration.
      {
        id: 'standort-vorlagen',
        label: 'Vorlagen',
        level: 2,
        rule: { prefixes: ['admin.vorlagen'], scope: 'location_exact' },
      },
      adminSection('admin-angebote', 'Globale Angebote', 'globale_angebote'),
      adminSection('admin-standorte', 'Standorte', 'standorte'),
      adminSection('admin-benutzer', 'Benutzer & Rollen', 'benutzer_und_rollen'),
      adminSection('admin-sicherheit', 'Sicherheit', 'sicherheit'),
      adminSection('admin-migration', 'Migration & Daten', 'migration_und_daten'),
      adminSection('admin-zahlungswesen', 'Zahlungswesen & Integrationen', 'zahlungswesen_und_integrationen'),
      adminSection('admin-vorlagen', 'Vorlagen', 'vorlagen'),
      adminSection('admin-oeffentlich', 'Öffentliche Bereiche', 'oeffentliche_bereiche'),
      adminSection('admin-system', 'System & Schnittstellen', 'system_und_schnittstellen'),
      adminSection('admin-storage', 'Storage & Dateien', 'storage_und_dateien'),
      adminSection('admin-datenschutz', 'Datenschutz', 'datenschutz'),
      adminSection('admin-logs', 'Protokollierung & Logs', 'protokollierung_und_logs'),
      adminSection('admin-ki', 'KI & Automatisierung', 'ki', 'automatisierung'),
      adminSection('admin-unternehmen', 'Unternehmen & Erscheinungsbild', 'unternehmen_und_erscheinungsbild'),
      adminSection('admin-verkaufsaktionen', 'Verkaufsaktionen', 'verkaufsaktionen'),
    ],
  },
  // Tickets (dauerhaft, auch live), Testdaten (nur Testsystem) und der
  // Abnahme-Katalog (docs/abnahme-plan.md) unter einem Punkt: was kaputt ist und
  // was fertig sein soll gehören zusammen. Kein Teil der Rechtematrix der
  // Fachabteilung. Welche Schalter an sind, rechnet /me/permissions heraus.
  {
    id: 'testing',
    label: 'Testing & Abnahme',
    level: 1,
    children: [
      { id: 'testing-tickets', label: 'Tickets', level: 2, rule: { prefixes: ['testing.tickets'] } },
      { id: 'abnahme-katalog', label: 'Abnahme', level: 2, rule: { prefixes: ['abnahme.katalog'] } },
      { id: 'abnahme-ablaeufe', label: 'Abläufe', level: 2, rule: { prefixes: ['abnahme.katalog'] } },
      { id: 'abnahme-statistik', label: 'Statistik', level: 2, rule: { prefixes: ['abnahme.katalog'] } },
      { id: 'abnahme-freigabe', label: 'Freigabe', level: 2, rule: { prefixes: ['abnahme.freigabe'] } },
      { id: 'abnahme-report', label: 'Report', level: 2, rule: { prefixes: ['abnahme.freigabe'] } },
      {
        id: 'abnahme-testlaeufe',
        label: 'Testläufe',
        level: 2,
        rule: { prefixes: ['abnahme.testlaeufe'] },
      },
      {
        id: 'testing-testdaten',
        label: 'Testdaten',
        level: 2,
        rule: { prefixes: ['testing.testdaten'] },
      },
    ],
  },
];

const isViewKey = (key: PermissionKey): boolean =>
  (key.split('.').pop() ?? '').includes('ansehen') && !NAV_EXCLUDED_KEYS.includes(key);

const VIEW_KEYS: readonly PermissionKey[] = PERMISSION_KEYS.filter(isViewKey);

const matches = (key: string, prefix: string): boolean => key === prefix || key.startsWith(`${prefix}.`);

function unlocks(permissions: EffectivePermissions, rule: NavRule): boolean {
  const unlocked = VIEW_KEYS.some((key) => {
    if (!rule.prefixes.some((prefix) => matches(key, prefix))) return false;
    if (!can(permissions, key)) return false;
    const grant = permissions[key];
    if (!grant) return false;
    const unrestrictedCompany = grant.scope === 'company' && grant.standort_ids === null;
    if (rule.scope === 'location_exact') return grant.scope === 'location';
    if (rule.scope === 'company_unrestricted') return unrestrictedCompany;
    if (rule.scope === 'location_or_company') return grant.scope === 'location' || unrestrictedCompany;
    return true;
  });
  if (!unlocked) return false;
  return !rule.alsoAnyOf || rule.alsoAnyOf.some((key) => can(permissions, key));
}

/** Sichtbare Menüpunkte; Gruppen nur mit mindestens einem sichtbaren Unterpunkt. */
export function deriveNavigation(permissions: EffectivePermissions | null | undefined): NavNode[] {
  if (!permissions) return [];
  return NAV_TREE.flatMap((node): NavNode[] => {
    if (node.children) {
      const children = node.children.filter((child) => child.rule && unlocks(permissions, child.rule));
      return children.length > 0 ? [{ ...node, children }] : [];
    }
    return node.rule && unlocks(permissions, node.rule) ? [node] : [];
  });
}

/** Sucht einen Menüpunkt (auch Unterpunkt) in einer abgeleiteten Navigation. */
export function findNavNode(nodes: readonly NavNode[], id: string | undefined): NavNode | undefined {
  for (const node of nodes) {
    if (node.id === id) return node;
    const child = node.children?.find((candidate) => candidate.id === id);
    if (child) return child;
  }
  return undefined;
}

/** Flache Liste (Label, Ebene) in Menüreihenfolge — Form der Soll-Navigation in Teil E. */
export function flattenNavigation(nodes: readonly NavNode[]): Array<{ label: string; level: 1 | 2 }> {
  return nodes.flatMap((node) => [
    { label: node.label, level: node.level },
    ...(node.children ?? []).map((child) => ({ label: child.label, level: child.level })),
  ]);
}
